import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useCkPortal } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { ChatMessageDto, ChatTreatmentStatus } from "../../api/shellApi";
import {
  AddReactionOutlinedIcon,
  ClearOutlinedIcon,
  DoneAllOutlinedIcon,
  DoneOutlinedIcon,
  FlagOutlinedIcon,
  HourglassEmptyOutlinedIcon,
  MarkChatUnreadOutlinedIcon,
  MoreVertOutlinedIcon,
  ReplyOutlinedIcon,
  TaskAltOutlinedIcon,
} from "../../ui/appIcons";
import { aggregateReactions, QUICK_REACTION_EMOJIS } from "./chatEmojiUtils";
import { ChatReplyPreview } from "./ChatReplyPreview";
import {
  TREATMENT_STATUS_LABELS,
  TREATMENT_STATUS_OPTIONS,
  TREATMENT_STATUS_STYLES,
  type TreatmentStatusValue,
} from "./chatTreatmentConstants";

type Props = {
  message: ChatMessageDto;
  mine: boolean;
  canSend: boolean;
  canManageTreatment: boolean;
  currentUserId: string | null;
  highlighted: boolean;
  onReply: (message: ChatMessageDto) => void;
  onReact: (messageId: string, emoji: string) => void;
  onDelete: (messageId: string) => void;
  onMarkUnread: (messageId: string) => void;
  onSetTreatment: (messageId: string, status: ChatTreatmentStatus | null) => void;
  onScrollToReply: (messageId: string) => void;
};

type FixedMenuPos = { top: number; left: number };

type FloatingKind = "more" | "reactions" | "treatment";

function canHoverFine(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );
}

/** Prefer below; clamp fully inside the viewport so chat overflow cannot clip it. */
function placeFloatingPanel(
  anchor: DOMRect,
  menuWidth: number,
  menuHeight: number,
  prefer: "below" | "above" = "below",
): FixedMenuPos {
  const gap = 6;
  const margin = 8;
  const spaceBelow = window.innerHeight - anchor.bottom - margin;
  const spaceAbove = anchor.top - margin;
  const wantBelow =
    prefer === "below"
      ? spaceBelow >= menuHeight || spaceBelow >= spaceAbove
      : spaceAbove < menuHeight && spaceBelow >= spaceAbove;

  const top = wantBelow
    ? Math.min(anchor.bottom + gap, window.innerHeight - menuHeight - margin)
    : Math.max(margin, anchor.top - menuHeight - gap);

  // Center on anchor, then clamp so nothing is cut off on either side.
  let left = anchor.left + anchor.width / 2 - menuWidth / 2;
  left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));
  return { top: Math.max(margin, top), left };
}

function TreatmentIcon({ status }: { status: TreatmentStatusValue }) {
  if (status === "handled") return <TaskAltOutlinedIcon />;
  if (status === "in_progress") return <HourglassEmptyOutlinedIcon />;
  return <FlagOutlinedIcon />;
}

function FloatingPortal({
  open,
  pos,
  panelRef,
  className,
  children,
}: {
  open: boolean;
  pos: FixedMenuPos | null;
  panelRef: RefObject<HTMLDivElement>;
  className: string;
  children: ReactNode;
}) {
  const portal = useCkPortal();
  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={panelRef}
      className={className}
      style={{ top: pos.top, left: pos.left }}
      role="menu"
    >
      {children}
    </div>,
    portal ?? document.body,
  );
}

export function ChatMessageBubble({
  message,
  mine,
  canSend,
  canManageTreatment,
  currentUserId,
  highlighted,
  onReply,
  onReact,
  onDelete,
  onMarkUnread,
  onSetTreatment,
  onScrollToReply,
}: Props) {
  const [hovered, setHovered] = useState(false);
  const [actionsPinned, setActionsPinned] = useState(false);
  const [openKind, setOpenKind] = useState<FloatingKind | null>(null);
  const [floatPos, setFloatPos] = useState<FixedMenuPos | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);
  const reactionBtnRef = useRef<HTMLButtonElement | null>(null);
  const treatmentBtnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const canShowActionBar = canSend || canManageTreatment;
  const showActions =
    (hovered || actionsPinned || openKind !== null) && canShowActionBar;

  const closeMenus = useCallback(() => {
    setOpenKind(null);
    setFloatPos(null);
  }, []);

  const updateFloatPos = useCallback(() => {
    if (!openKind) return;
    const btn =
      openKind === "more"
        ? moreBtnRef.current
        : openKind === "reactions"
          ? reactionBtnRef.current
          : treatmentBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    if (openKind === "reactions") {
      setFloatPos(placeFloatingPanel(rect, 236, 48, "below"));
    } else if (openKind === "treatment") {
      setFloatPos(placeFloatingPanel(rect, 180, 160, "below"));
    } else {
      const itemCount = mine && canSend ? 2 : 1;
      setFloatPos(placeFloatingPanel(rect, 170, 12 + itemCount * 36, "below"));
    }
  }, [openKind, mine, canSend]);

  useLayoutEffect(() => {
    if (!openKind) return;
    updateFloatPos();
    const onReposition = () => updateFloatPos();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [openKind, updateFloatPos]);

  useEffect(() => {
    if (!openKind && !actionsPinned) return;
    const onDoc = (e: PointerEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      closeMenus();
      setActionsPinned(false);
      setHovered(false);
    };
    document.addEventListener("pointerdown", onDoc, true);
    return () => document.removeEventListener("pointerdown", onDoc, true);
  }, [openKind, actionsPinned, closeMenus]);

  const toggleKind = (kind: FloatingKind) => {
    setOpenKind((prev) => (prev === kind ? null : kind));
    if (kind === openKind) setFloatPos(null);
  };

  const aggregated = aggregateReactions(message.reactions, currentUserId);
  const treatment = message.treatmentStatus ?? null;
  const treatmentStyle = treatment ? TREATMENT_STATUS_STYLES[treatment] : null;

  const handleBubbleTap = (e: MouseEvent) => {
    if (!canShowActionBar || canHoverFine()) return;
    const target = e.target as HTMLElement;
    if (target.closest("button, a, [role='menuitem']")) return;
    setActionsPinned((prev) => !prev);
  };

  return (
    <div
      ref={rootRef}
      id={`chat-msg-${message.id}`}
      className={`chat-bubble-row${mine ? " chat-bubble-row--mine" : ""}${
        highlighted ? " chat-bubble-row--highlight" : ""
      }`}
      onMouseEnter={() => {
        if (canHoverFine()) setHovered(true);
      }}
      onMouseLeave={() => {
        if (canHoverFine() && openKind === null) {
          setHovered(false);
        }
      }}
    >
      <div className="chat-bubble-row__main">
        {showActions ? (
          <div
            className={`chat-bubble__float-actions${
              mine ? " chat-bubble__float-actions--mine" : ""
            }`}
          >
            {canSend ? (
              <>
                <button
                  type="button"
                  className="chat-bubble__icon-btn"
                  aria-label="תגובה"
                  title="תגובה"
                  onClick={() => {
                    setActionsPinned(false);
                    closeMenus();
                    onReply(message);
                  }}
                >
                  <ReplyOutlinedIcon />
                </button>
                <button
                  ref={reactionBtnRef}
                  type="button"
                  className="chat-bubble__icon-btn"
                  aria-label="תגובה באימוג'י"
                  title="תגובה באימוג'י"
                  aria-expanded={openKind === "reactions"}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleKind("reactions");
                  }}
                >
                  <AddReactionOutlinedIcon />
                </button>
              </>
            ) : null}

            {canManageTreatment ? (
              <button
                ref={treatmentBtnRef}
                type="button"
                className="chat-bubble__icon-btn"
                aria-label="סטטוס טיפול"
                title={
                  treatment ? TREATMENT_STATUS_LABELS[treatment] : "סמן לטיפול"
                }
                style={treatmentStyle ? { color: treatmentStyle.color } : undefined}
                aria-expanded={openKind === "treatment"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleKind("treatment");
                }}
              >
                <FlagOutlinedIcon />
              </button>
            ) : null}
          </div>
        ) : null}

        <div
          className={`chat-bubble${mine ? " chat-bubble--mine" : ""}${
            canShowActionBar && !canHoverFine() ? " chat-bubble--tappable" : ""
          }`}
          onClick={handleBubbleTap}
        >
          {message.replySnapshot ? (
            <button
              type="button"
              className="chat-bubble__quote"
              onClick={(e) => {
                e.stopPropagation();
                if (message.replyToMessageId) onScrollToReply(message.replyToMessageId);
              }}
            >
              <ChatReplyPreview snapshot={message.replySnapshot} compact />
            </button>
          ) : null}

          <div className="chat-bubble__content">{message.content}</div>

          {treatment && treatmentStyle ? (
            <span
              className="chat-bubble__treatment"
              style={{
                background: treatmentStyle.bg,
                color: treatmentStyle.color,
                borderColor: treatmentStyle.border,
              }}
            >
              <TreatmentIcon status={treatment} />
              {TREATMENT_STATUS_LABELS[treatment]}
            </span>
          ) : null}
        </div>

        {mine ? (
          <div className="chat-bubble__meta chat-bubble__meta--mine">
            <span
              className={`chat-bubble__delivery chat-bubble__delivery--${
                message.deliveryStatus ?? "sent"
              }`}
              title={
                message.deliveryStatus === "read"
                  ? "נקרא"
                  : message.deliveryStatus === "delivered"
                    ? "נמסר"
                    : "נשלח"
              }
            >
              {message.deliveryStatus === "read" ||
              message.deliveryStatus === "delivered" ? (
                <DoneAllOutlinedIcon />
              ) : (
                <DoneOutlinedIcon />
              )}
            </span>
          </div>
        ) : null}

        {aggregated.length > 0 ? (
          <div
            className={`chat-bubble__reactions${
              mine ? " chat-bubble__reactions--mine" : ""
            }`}
          >
            {aggregated.map((r) => (
              <button
                key={r.emoji}
                type="button"
                className={`chat-bubble__reaction-chip${r.reactedByMe ? " is-mine" : ""}`}
                disabled={!canSend}
                onClick={() => onReact(message.id, r.emoji)}
                title={`${r.count} תגובות`}
              >
                {r.emoji} {r.count > 1 ? r.count : ""}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="chat-bubble__side-menu">
        <button
          ref={moreBtnRef}
          type="button"
          className="chat-bubble__icon-btn chat-bubble__icon-btn--side"
          aria-label="עוד פעולות"
          title="עוד פעולות"
          aria-expanded={openKind === "more"}
          onClick={(e) => {
            e.stopPropagation();
            toggleKind("more");
          }}
        >
          <MoreVertOutlinedIcon />
        </button>
      </div>

      <FloatingPortal
        open={openKind === "reactions"}
        pos={floatPos}
        panelRef={panelRef}
        className="chat-quick-reactions chat-float-portal"
      >
        {QUICK_REACTION_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            role="menuitem"
            onClick={() => {
              closeMenus();
              setActionsPinned(false);
              onReact(message.id, emoji);
            }}
          >
            {emoji}
          </button>
        ))}
      </FloatingPortal>

      <FloatingPortal
        open={openKind === "treatment"}
        pos={floatPos}
        panelRef={panelRef}
        className="chat-message-menu chat-float-portal"
      >
        {TREATMENT_STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="menuitem"
            onClick={() => {
              closeMenus();
              setActionsPinned(false);
              onSetTreatment(message.id, opt.value);
            }}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            closeMenus();
            setActionsPinned(false);
            onSetTreatment(message.id, null);
          }}
        >
          <ClearOutlinedIcon /> הסר סימון
        </button>
      </FloatingPortal>

      <FloatingPortal
        open={openKind === "more"}
        pos={floatPos}
        panelRef={panelRef}
        className="chat-message-menu chat-float-portal"
      >
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            closeMenus();
            onMarkUnread(message.id);
          }}
        >
          <MarkChatUnreadOutlinedIcon /> סמן כלא נקרא
        </button>
        {mine && canSend ? (
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              closeMenus();
              onDelete(message.id);
            }}
          >
            מחק
          </button>
        ) : null}
      </FloatingPortal>
    </div>
  );
}
